import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import AiLabel from "@/components/AiLabel";
// Start-Karten nutzen eigene, verkleinerte WebP-Varianten (1280 px, q85).
// Die Originale bleiben unangetastet — sie sind die Heros der Unterseiten.
import kiImg from "@/assets/home/ki-start-hero-card.webp";
import bessImg from "@/assets/home/bess-aerial-substation-card.webp";
import assetImg from "@/assets/home/asset-management-hero-card.webp";

interface CardProps {
  eyebrow: string;
  title: string;
  desc: string;
  image: string;
  ctaLabel: string;
  to: string;
  external?: boolean;
}

const Card = ({ eyebrow, title, desc, image, ctaLabel, to, external }: CardProps) => {
  const inner = (
    <>
      <div className="relative aspect-[16/10] overflow-hidden">
        <img
          src={image}
          alt={title}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <AiLabel tone="auto" />
      </div>
      <div className="p-6">
        <p className="text-xs font-semibold tracking-wider uppercase text-primary mb-2">
          {eyebrow}
        </p>
        <h3 className="text-xl font-bold text-ci-forest mb-3 leading-snug">
          {title}
        </h3>
        <p className="text-sm text-foreground/70 mb-4 leading-relaxed">
          {desc}
        </p>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary group-hover:gap-2 transition-all">
          {ctaLabel} <ArrowRight className="w-4 h-4" />
        </span>
      </div>
    </>
  );

  const className =
    "group block bg-white rounded-2xl overflow-hidden shadow-sm border border-border hover:shadow-xl hover:-translate-y-1 transition-all duration-300";

  return external ? (
    <a href={to} target="_blank" rel="noopener noreferrer" className={className}>
      {inner}
    </a>
  ) : (
    <Link to={to} className={className}>
      {inner}
    </Link>
  );
};

const WhatWeDoSection = () => {
  const { t } = useLanguage();

  return (
    <section className="py-16 md:py-24 bg-muted/40">
      <div className="container-custom">
        <p className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-primary mb-3">
          {t("home.what.eyebrow")}
        </p>
        <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-ci-forest mb-12 max-w-4xl leading-[1.1]">
          {t("home.what.title")}
        </h2>

        <div className="grid md:grid-cols-3 gap-6">
          <Card
            eyebrow={t("home.what.card1.eyebrow")}
            title={t("home.what.card1.title")}
            desc={t("home.what.card1.desc")}
            image={kiImg}
            ctaLabel={t("home.what.card1.cta")}
            to="/ki"
          />
          <Card
            eyebrow={t("home.what.card2.eyebrow")}
            title={t("home.what.card2.title")}
            desc={t("home.what.card2.desc")}
            image={bessImg}
            ctaLabel={t("home.what.card2.cta")}
            to="/gridstable"
          />
          <Card
            eyebrow={t("home.what.card3.eyebrow")}
            title={t("home.what.card3.title")}
            desc={t("home.what.card3.desc")}
            image={assetImg}
            ctaLabel={t("home.what.card3.cta")}
            to="/plattform"
          />
        </div>
      </div>
    </section>
  );
};

export default WhatWeDoSection;
